import { useEffect, useState, type ReactNode } from "react";
import { Card, CardContent, EmptyState, Skeleton } from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { StatementDownloadBar } from "../components/StatementDownloadBar";
import {
  portalBrokerFinanceApi,
  type BrokerCommissionEvent,
  type BrokerCommissionLoanRow,
  type BrokerFinanceSummary,
  type BrokerLoanCommissionDetail,
} from "../api/brokerFinance";

// External party portals — Broker "Commissions" (PR-C). A visual roll-up of the
// broker's commission earned to date with a per-loan breakdown that drills into
// each loan's payout history. Read-only over the recognized waterfall broker
// carve — loans with no recognized commission simply don't appear.

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-space-1">
        <p className="text-caption uppercase tracking-wide text-gray-mid">{label}</p>
        <div className="text-h4 font-semibold text-onyx">{children}</div>
      </CardContent>
    </Card>
  );
}

function LoanCard({
  loan,
  onOpen,
}: {
  loan: BrokerCommissionLoanRow;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`broker-loan-card-${loan.loanId}`}
      className="block w-full rounded-lg border border-border-light bg-white-pure p-space-4 text-left transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <div className="flex items-center justify-between gap-space-3">
        <span className="text-small font-semibold text-onyx">{loan.loanNumber}</span>
        <span className="text-small font-semibold text-onyx">
          {formatCurrency(loan.totalEarned)}
        </span>
      </div>
      <div className="mt-space-2 flex flex-wrap gap-space-3 text-caption text-gray-mid">
        <span>
          {loan.paymentCount} payment{loan.paymentCount === 1 ? "" : "s"}
        </span>
        {loan.lastPaidAt && <span>Last paid {formatDateInTz(loan.lastPaidAt)}</span>}
      </div>
    </button>
  );
}

function EventsTable({ events }: { events: BrokerCommissionEvent[] }) {
  if (events.length === 0) {
    return (
      <EmptyState
        title="No payouts yet"
        description="Commission payouts for this loan will appear here once it begins paying."
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border-light">
      <table className="w-full text-small">
        <thead>
          <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
            <th className="px-space-3 py-space-2 font-medium">Paid</th>
            <th className="px-space-3 py-space-2 font-medium">Period</th>
            <th className="px-space-3 py-space-2 font-medium">Type</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i} className="border-b border-border-light last:border-0">
              <td className="px-space-3 py-space-2 text-onyx">{formatDateInTz(e.recognizedAt)}</td>
              <td className="px-space-3 py-space-2 text-gray-mid">
                {e.periodStart ? formatCalendarDate(e.periodStart) : "—"}
              </td>
              <td className="px-space-3 py-space-2 text-gray-mid">{e.typeLabel}</td>
              <td className="px-space-3 py-space-2 text-right text-onyx">
                {formatCurrency(e.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoanCommissionView({ loanId, onBack }: { loanId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<BrokerLoanCommissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    void (async () => {
      try {
        const data = await portalBrokerFinanceApi.loan(loanId);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load this loan's commissions.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  return (
    <div className="space-y-space-5">
      <button
        type="button"
        onClick={onBack}
        data-testid="broker-commissions-back"
        className="text-small font-medium text-gold hover:underline"
      >
        ← Back to all loans
      </button>
      {error && <p className="text-small text-status-error">{error}</p>}
      {!detail && !error && <Skeleton className="h-40 w-full" />}
      {detail && (
        <>
          <div className="flex items-center justify-between gap-space-3">
            <h3 className="text-h4 font-semibold text-onyx">{detail.loanNumber}</h3>
            <span className="text-h4 font-semibold text-onyx">
              {formatCurrency(detail.totalEarned)}
            </span>
          </div>
          <EventsTable events={detail.events} />
        </>
      )}
    </div>
  );
}

export function BrokerCommissionsTab() {
  const [summary, setSummary] = useState<BrokerFinanceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalBrokerFinanceApi.summary();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your commissions.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (selectedLoanId) {
    return <LoanCommissionView loanId={selectedLoanId} onBack={() => setSelectedLoanId(null)} />;
  }

  if (error) return <p className="text-small text-status-error">{error}</p>;

  if (!summary) {
    return (
      <div
        className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="broker-commissions-loading"
      >
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (summary.financialsHidden) {
    return (
      <EmptyState
        title="Commission figures aren't shown"
        description="Your brokerage manages your commissions and hasn't enabled figures for your account. Reach out to your firm for details on what you're owed."
      />
    );
  }

  if (summary.loanCount === 0) {
    return (
      <EmptyState
        title="No commissions yet"
        description="Your commissions appear here once your brokered loans are funded and begin paying. Loans without broker fee terms won't show a commission."
      />
    );
  }

  return (
    <div className="space-y-space-5">
      <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Commission earned to date">{formatCurrency(summary.totalEarned)}</Stat>
        <Stat label="Brokered loans paying">{summary.loanCount}</Stat>
        <Stat label="Payments">{summary.paymentCount}</Stat>
        <Stat label="Last paid">
          {summary.lastPaidAt ? formatDateInTz(summary.lastPaidAt) : "—"}
        </Stat>
      </div>

      <StatementDownloadBar
        onDownload={(p) => portalBrokerFinanceApi.statement(p)}
        filename={(p) => `commission-statement-${p}.pdf`}
      />

      <div className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
          Commission by loan ({summary.loanCount})
        </h3>
        {summary.loans.map((loan) => (
          <LoanCard key={loan.loanId} loan={loan} onOpen={() => setSelectedLoanId(loan.loanId)} />
        ))}
      </div>
    </div>
  );
}
